import { daysBetween } from '../../../../../utils/plan/companyPlanModel';

/**
 * KAN-196 — the approved Stripe-era wording of the KAN-847 locked card (past
 * due / read-only / suspended / trial ended / subscription ended). ONLY used on
 * the billing-available path; the email path keeps the older copy. Returns the
 * words to swap in ({ headline, sentence, markLabel } — any may be missing, then
 * the card's own wording stands) or null. Pure given `intl`.
 * `date(ymd)` formats a friendly day; `todayYmd` is the company's today.
 */
export default function stripeLockCopy({
  intl, level, base, status, access, trialEndsAt, todayYmd, date,
}) {
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const stepYmd = access?.nextStepOn || null;
  if (level === 'grace' && base === 'past_due') {
    const left = stepYmd ? Math.max(0, daysBetween(todayYmd, stepYmd) ?? 0) : null;
    return {
      markLabel: t('billing.lock.pastDue.mark', 'Payment failed'),
      headline: t('billing.lock.pastDue.headline', "We couldn't charge your card"),
      sentence: left === null ? null : t(
        'billing.lock.pastDue.sentence',
        'Full access continues for {count, plural, one {# more day} other {# more days}} while Stripe retries. Update the card to settle it now.',
        { count: left },
      ),
    };
  }
  if (level === 'read_only') {
    if (base === 'past_due') {
      return { sentence: t('billing.lock.readOnly.pastDue', 'The last payment never went through. Update the card and everything comes back right away.') };
    }
    if (base === 'cancelled') {
      const ended = status?.paidThrough;
      return {
        headline: t('billing.lock.ended.headline', 'Subscription ended'),
        sentence: ended ? t('billing.lock.ended.sentence', 'Your plan ended on {date}. Resubscribe to get full access back. Your data is safe.', { date: date(ended) }) : null,
      };
    }
    if (base === 'trial' || base === 'none') {
      return {
        headline: t('billing.lock.trialEnded.headline', 'Trial ended'),
        sentence: trialEndsAt ? t('billing.lock.trialEnded.sentence', 'Your trial ended on {date}. Subscribe to get full access back.', { date: date(trialEndsAt) }) : null,
      };
    }
    return null;
  }
  if (level === 'suspended') {
    return {
      headline: t('billing.lock.suspended.headline', 'Account suspended'),
      sentence: base === 'past_due'
        ? t('billing.lock.suspended.pastDue', 'Payment has been overdue for a while, so access is paused. Update payment to turn everything back on.')
        : null,
    };
  }
  return null;
}

/**
 * KAN-865 — the suspended card's sentence follows the pay button actually shown. Literal ids
 * (KAN-855: the i18n guards scan literals). Returns { id, defaultMessage } or null (no pay
 * action: the caller keeps its own wording).
 */
export function suspendedLockBody({ action, kind }) {
  // A Stripe-linked manage / resume while locked is the healthy-but-locked mismatch (KAN-867): the screen
  // shows its own sentence there, so only past due (portal) and the chooser kinds have a body here.
  if (action === 'portal' && kind === 'updatePayment') {
    return { id: 'billing.lock.suspended.pastDue', defaultMessage: 'Payment has been overdue for a while, so access is paused. Update payment to turn everything back on.' };
  }
  if (action === 'chooser') {
    return kind === 'resubscribe'
      ? { id: 'billing.lock.suspended.resubscribe', defaultMessage: 'Resubscribe to start using CrewVex again.' }
      : { id: 'companyAccess.screen.bodyAdmin.subscribe', defaultMessage: 'Subscribe to start using CrewVex again.' };
  }
  return null;
}
